<template>
  <div class="c-problem-location">
    <div class="c-form__group">
      <radio-group
        v-if="showIsAtProperty"
        id="isAtProperty"
        :label="isProblemAtAddressLabel"
        v-on:change="problemAtAddressChange"
      />
    </div>
    <div v-if="showOutOfPropertyLookup">
      <h3
        class="c-problem-location__title"
        v-if="locateProblemOutOfPropertyTitle"
      >
        {{ locateProblemOutOfPropertyTitle }}
      </h3>
      <div
        v-if="locateProblemOutOfPropertyDesc"
        v-html="locateProblemOutOfPropertyDesc"
      ></div>

      <property-lookup
        id="addressLookup"
        key="addressLookup"
        :lookupFormatError="postcodeFormatError"
        :lookupLabel="lookupPostcodeLabel"
        :searchButton="searchButtonLabel"
        :customersOnly="customersOnly"
        :notListedError="postcodeNotListed"
        :notAllowedError="postcodeNotAllowed"
        @selectedAddress="updateAddress"
        @locationValid="setLocationValid"
        @onSubmit="propertyLookupSubmit"
      />
    </div>

    <div v-if="showProblemAtProperty">
      <h3 class="c-problem-location__title" v-if="locateProblemAtPropertyTitle">
        {{ locateProblemAtPropertyTitle }}
      </h3>

      <div
        v-if="locateProblemAtPropertyDesc"
        v-html="locateProblemAtPropertyDesc"
      ></div>

      <div class="c-form__group">
        <radio-list
          id="locationProblemOptions"
          :options="locateProblemOptions"
          :label="locateProblemAtPropertyLabel"
          @change="locationProblemChange"
        />
      </div>

      <property-lookup
        v-if="showCustomerReferenceLookup"
        id="customerReferenceLookup"
        key="customerReferenceLookup"
        :lookupFormatError="customerReferenceFormatError"
        :lookupLabel="lookupCustomerReferenceLabel"
        :searchButton="searchButtonLabel"
        :isCustomerReference="true"
        :customersOnly="customersOnly"
        :notListedError="customerReferenceNotListed"
        :notAllowedError="customerReferenceNotAllowed"
        @selectedAddress="updateAddress"
        @locationValid="setLocationValid"
        @onSubmit="propertyLookupSubmit"
        @onLookUpValue="customerRefenceUpdated"
      />

      <property-lookup
        v-if="showPropertyLookup"
        id="propertyLookup"
        key="propertyLookup"
        :lookupFormatError="postcodeFormatError"
        :lookupLabel="lookupPostcodeLabel"
        :searchButton="searchButtonLabel"
        :customersOnly="customersOnly"
        :notListedError="postcodeNotListed"
        :notAllowedError="postcodeNotAllowed"
        @selectedAddress="updateAddress"
        @locationValid="setLocationValid"
        @onSubmit="propertyLookupSubmit"
      />
    </div>
    <action-buttons
      :showPrev="false"
      :nextLabel="continueButtonText"
      :nextDisabled="nextIsDisabled"
      :showNext="true"
      @onNext="$emit('stepChange', 1)"
    />
  </div>
</template>

<script>
  import ActionButtons from "../../forms/action-buttons/ActionButtons.vue";
  import PropertyLookup from "../../property-lookup/PropertyLookup.vue";
  import LoadingSpinner from "Components/reportAProblem/_vue-shared/LoadingSpinner.vue";
  import RadioGroup from "../../forms/radio-group/RadioGroup.vue";
  import RadioList from "../../forms/radio-list/RadioList.vue";
  import TextField from "../../forms/input/TextField.vue";
  import rteMixin from "./mixins/rteMixin.js";

  import axios from "axios";
  import endpoints from "Globals/mando.endpoints";

  export default {
    mixins: [rteMixin],
    components: {
      ActionButtons,
      PropertyLookup,
      LoadingSpinner,
      RadioGroup,
      RadioList,
      TextField,
    },
    props: {
      backButtonText: {
        required: true,
        type: String,
      },
      continueButtonText: {
        required: true,
        type: String,
      },
      showIsAtProperty: {
        type: Boolean,
      },
      isAtProperty: {
        type: String,
      },
      isProblemAtAddressLabel: {
        type: String,
        default: "Is the problem at your property?",
      },
      isProblemAtAddressId: {
        type: String,
        default: "problem-at-adress-id",
      },
      lookupPostcodeLabel: {
        type: String,
        default: "Postcode lookup",
      },
      lookupCustomerReferenceLabel: {
        type: String,
        default: "Customer reference number",
      },
      lookupPostcodeDesc: {
        type: String,
        default:
          "Please enter your postcode to find your address, enter the postcode below",
      },
      postcodeNotListed: {
        type: String,
        default: "<p>Sorry! We don't recognise your postcode</p>"
      },
      postcodeNotAllowed: {
        type: String,
        default: `Sorry! You are not in our supply area.<br>You can use the <a href="https://www.water.org.uk/customers/find-your-supplier" target="_blank" rel="noreferrer">Water UK website</a> to find your supplier or <a href="https://www.wessexwater.co.uk/contact-us" target="_blank" rel="noreferrer">contact us</a>`
      },
      postcodeFormatError: {
        type: String,
        default: "<p>Please enter a valid postcode</p>"
      },
      customerReferenceNotListed: {
        type: String,
        default: "<p>Sorry! We don't recognise your customer reference number</p>"
      },
      customerReferenceNotAllowed: {
        type: String,
        default: `Sorry! You are not in our supply area.<br>You can use the <a href="https://www.water.org.uk/customers/find-your-supplier" target="_blank" rel="noreferrer">Water UK website</a> to find your supplier or <a href="https://www.wessexwater.co.uk/contact-us" target="_blank" rel="noreferrer">contact us</a>.`
      },
      customerReferenceFormatError: {
        type: String,
        default: "<p>Your customer number must be 8 digits</p>"
      },
      searchButtonLabel: {
        type: String,
      },
      locateProblemAtPropertyTitle: {
        type: String,
        default: "Help us locate the problem",
      },
      locateProblemAtPropertyLabel: {
        type: String,
        default: "Please enter your customer reference number or postcode."
      },
      locateProblemAtPropertyDesc: {
        type: String,
      },
      locateProblemOutOfPropertyTitle: {
        type: String,
        default: "Help us locate the problem",
      },
      locateProblemOutOfPropertyDesc: {
        type: String,
      },
      customerReferencOptionLabel: {
        type: String,
        default: "Customer reference",
      },
      postcodeLookupOptionLabel: {
        type: String,
        default: "Postcode lookup",
      },
      customersOnly: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        defaultAddress: {
          value: "",
          label: "",
          wessexProvider: "",
        },
        address: {
          value: "",
          label: "",
          wessexProvider: "",
        },
        boundaries: null,
        addressSubmitted: false,
        customerReference: "",
        postcode: "",
        searchSubmitted: false,
        locateProblemOptions: [
          {
            label: this.customerReferencOptionLabel,
            value: "customer-reference",
          },
          {
            label: this.postcodeLookupOptionLabel,
            value: "postcode-lookup",
          },
        ],
        locationProblemOption: "",
        locationValid: false,
      };
    },
    computed: {
      showProblemAtProperty() {
        return this.isAtProperty === "Yes";
      },
      showOutOfPropertyLookup() {
        return this.isAtProperty === "No";
      },
      showPropertyLookup() {
        return this.locationProblemOption === "postcode-lookup";
      },
      showCustomerReferenceLookup() {
        return this.locationProblemOption === "customer-reference";
      },
      customerReferenceError() {
        return this.customerReferenceNumber === ""
          ? false
          : !this.isValidCustomerReference(this.customerReference);
      },
      nextIsDisabled() {
        return !this.locationValid || !this.address.value;
      },
    },
    methods: {
      customerRefenceUpdated(value) {
        this.$emit("updateCustomerReference", value);
      },
      propertyLookupSubmit() {
        if (this.address.value) {
          this.resetAddress();
        }
      },
      setLocationValid(value) {
        this.locationValid = value;
      },
      resetAddress() {
        this.address = this.defaultAddress;
        this.$emit("updateAddress", this.address);
      },
      locationProblemChange(index, option) {
        this.locationProblemOption = option.value;
        this.resetAddress();
      },
      async loadBoundaries() {
        try {
          const response = await axios.get(endpoints("getBoundaries"));
          this.boundaries = response.data.results[0];
        } catch (error) {
          console.error("Error fetching boundaries:", error);
        }
      },
      problemAtAddressChange(index, option) {
        this.resetAddress();
        this.setLocationValid(false);
        this.locationProblemOption = "";
        this.isAtProperty = option.label;
        this.$emit("isAtPropertyChange", option.label);
      },
      isValidPostcode(value) {
        const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/;
        return postcodeRegex.test(value);
      },
      isFieldValid(fieldName, value) {
        if (fieldName === "propertyPostcode") {
          if (!this.isValidPostcode(value)) {
            return {
              valid: false,
              invalid: true,
            };
          } else {
            return {
              invalid: false,
              valid: true,
            };
          }
        } else {
          if (value.trim() !== "") {
            return {
              invalid: false,
              valid: true,
            };
          } else {
            return {
              valid: false,
              invalid: true,
            };
          }
        }
      },
      updateAddress(address) {
        if(this.locationValid) {
          this.address = address;
          this.$emit("updateAddress", this.address);
        }
      },
    },
    watch: {
      manualAddressFields(newFields) {
        this.validatedAddress(newFields);
      },
      addressFields(newFields) {
        this.validatedAddress(newFields);
      },
    },
    mounted() {
      this.loadBoundaries();
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/components/reportAProblem/_contact-us-rap/_vue/ProblemLocation.vue